import i18n from './i18n'

export const TIMEZONE = 'Asia/Tokyo'
export const ROOT = '/' as const
export const ERROR = 'error' as const

export const VILLA_BRAMARE_SLUG = 'villa_bramare'

export const DEFAULT_ROOMS = 1
export const DEFAULT_ADULTS = 2
export const DEFAULT_CHILDREN = 0
export const DEFAULT_INFANTS = 0
export const DEFAULT_TRANSACTION_ID = 0
export const DEFAULT_BUDGET_MAX = 0
export const DEFAULT_BUDGET_MIN = 0
export const PAGE_SIZE = 12

// Origin of the production site. Used to gate features (e.g. third-party tags,
// unreleased UI) that must behave differently on production than on
// staging/preview, which share the same production build.
export const PRODUCTION_ORIGIN = 'https://booking.hkdballpark.com'

// Maximum number of nights for a single reservation. The booking backend
// (TL-Lincoln) caps a stay at 99 nights, so the date-range picker must not let
// a guest select a check-out more than this many nights after check-in.
export const MAX_NIGHTS = 99

// Single source of truth for area values.
// To add or remove an area, update this list (and the `hooks.useArea`
// translation). The `Area` type, `DEFAULT_AREA`, and `useArea` all derive
// from it.
export const AREA_VALUES = [
  'fvillage',
  'kitahiroshima',
  'shinsapporo',
  'sapporo',
  'chitose',
  'others',
] as const

export const DEFAULT_AREA: string[] = [...AREA_VALUES]

export const COUPON_NEWS = {
  ja: {
    name: '最大12％オフ・サマーキャンペーン実施中！',
    description:
      '夏の期間限定のサマーキャンペーンを実施中！\n' +
      'Fビレッジ近郊施設での宿泊が最大12％オフで、お得にご利用いただけます。',
    descriptionDetail:
      '北海道ボールパークFビレッジ内およびFビレッジ近郊宿泊施設が予約できる「Fビレッジ宿泊予約サイト」では、夏の期間限定のサマーキャンペーンを実施中！\n' +
      '\n' +
      'Fビレッジ近郊施設での宿泊が最大12％オフで、お得にご利用いただけます。',
    shortName: '最大12％オフ・サマーキャンペーン実施中！',
    access: 'キャンペーン対象 全16施設',
  },
  en: {
    name: 'Up to 12% Off – Summer Campaign Now On!',
    description:
      'Enjoy our limited-time summer campaign!\n' +
      'Get up to 12% off stays at accommodations near F Village.',
    descriptionDetail:
      '"F Village Booking Site" offers a special limited-time summer campaign for stays at properties within and around Hokkaido Ballpark F Village.\n' +
      '\n' +
      'Take advantage of up to 12% off when booking accommodations near F Village.',
    shortName: 'Up to 12% Off – Summer Campaign Now On!',
    access: 'All 16 participating hotels',
  },
  images: ['/img/summer-campaign.png'] as string[],
  pricing: '',
  property: 'summer-campaign',
  externalUrl: 'https://www.hkdballpark.com/news/717/',
} as const

export const MASTERSVARUS_FACILITY = {
  ja: {
    name: 'マスターズヴェラス 北海道ボールパーク',
    description:
      'あたらしい人生を Fビレッジで。\n' +
      '自分らしいスタイルで自由闊達に人生を謳歌する "マスターズ" のための拠点。\n' +
      '\n' +
      'シニアライフを安心で安全に過ごされるためのお住まいとして、介護が必要となった時にも安心して暮らせる「サービス付高齢者住宅」が Fビレッジにはあります。\n' +
      '\n' +
      '＊「体験入居キャンペーン」を実施しております。\n' +
      'この機会にFビレッジでの暮らしをどうぞゆっくりとご体験ください。',
    descriptionDetail:
      'あたらしい人生を Fビレッジで。\n' +
      '自分らしいスタイルで自由闊達に人生を謳歌する "マスターズ" のための拠点。\n' +
      '\n' +
      'シニアライフを安心で安全に過ごされるためのお住まいとして、介護が必要となった時にも安心して暮らせる「サービス付高齢者住宅」が Fビレッジにはあります。\n' +
      '\n' +
      '＊「体験入居キャンペーン」を実施しております。\n' +
      'この機会にFビレッジでの暮らしをどうぞゆっくりとご体験ください。',
    shortName: 'マスターズヴェラス',
    access: 'Fビレッジ 敷地内',
  },
  en: {
    name: 'MASTERS VARUS HOKKAIDO BALLPARK',
    description:
      'A New Chapter of Life at F Village\n' +
      'F Village is your base for living freely and vibrantly in your own style — a place designed for the “Masters” who seek to enjoy life to the fullest.\n' +
      '\n' +
      'Our senior-friendly rental housing with support services offers a safe and secure environment, ensuring peace of mind even when care becomes necessary. ' +
      '＊ We are currently offering a Trial Stay Campaign.\n' +
      '\n' +
      'Take this opportunity to experience life at F Village at your own pace and discover a new way of living.',
    descriptionDetail:
      'A New Chapter of Life at F Village\n' +
      'F Village is your base for living freely and vibrantly in your own style — a place designed for the “Masters” who seek to enjoy life to the fullest.\n' +
      '\n' +
      'Our senior-friendly rental housing with support services offers a safe and secure environment, ensuring peace of mind even when care becomes necessary. ' +
      '＊ We are currently offering a Trial Stay Campaign.\n' +
      '\n' +
      'Take this opportunity to experience life at F Village at your own pace and discover a new way of living.',
    shortName: 'MASTERS VARUS',
    access: 'Inside F Village',
  },
  images: ['/img/mastersvarus.png'] as string[],
  pricing: '',
  property: 'mastersvarus',
  externalUrl: 'https://mastersvarus.jp/',
} as const

export const reservationRouteType = {
  Hotel: 'hotels',
  Spa: 'spas',
  Activity: 'activities',
} as const

export const getReservationPaths = (
  type: (typeof reservationRouteType)[keyof typeof reservationRouteType]
) => ({
  root: `/${type}/reservations`,
  history: `/${type}/reservations/history`,
  reservation: `/${type}/reservations/:reservation`,
  cancel: {
    root: `/${type}/reservations/:reservation/cancel`,
    completion: `/${type}/reservations/:reservation/cancel/completion`,
  },
})

export const PATH = {
  root: ROOT,
  list: '/list',
  schedule: '/schedule',
  search: '/search',
  login: {
    root: '/login/callback',
    fid: '/login/callback/fid',
    google: '/login/callback/google',
  },
  property: '/:property',
  registrations: {
    root: '/:property/:room/:plan/registrations',
    confirmation: 'confirmation',
    completion: 'completion',
    loading: 'loading',
  },
  rooms: '/:property/rooms',
  plans: '/:property/:room/plans',
  // Facility-wide plan index (plan-first entry). Distinct from the room-scoped
  // `plans` above; each plan card links to `plan.rooms` (`/:property/:plan/rooms`).
  propertyPlans: '/:property/plans',
  options: '/:property/:room/:plan/options',
  plan: {
    rooms: '/:property/:plan/rooms',
  },
  reservations: {
    hotels: getReservationPaths(reservationRouteType.Hotel),
    spas: getReservationPaths(reservationRouteType.Spa),
    activities: getReservationPaths(reservationRouteType.Activity),
  },
  errors: {
    loginError: '/login/error',
    notFoundError: '/404',
    serverError: '/500',
  },
}

const REGISTRATION_ROOT = PATH.registrations.root

export const REGISTRATION_PATH = {
  registrations: `${REGISTRATION_ROOT}`,
  confirmation: `${REGISTRATION_ROOT}/${PATH.registrations.confirmation}`,
  completion: `${REGISTRATION_ROOT}/${PATH.registrations.completion}`,
  loading: `${REGISTRATION_ROOT}/${PATH.registrations.loading}`,
}

// Spa(onyoku) page's header logo link
// If activity page(e.g. dogrun page) was added,
// add external link page to below.
export const EXTERNAL_PATH = {
  spas: 'https://www.hkdballpark.com/shops/10/',
  activities: 'https://www.hkdballpark.com/news/425/',
}

export const meta = {
  root: {
    title: i18n.t('meta.pages.root.title'),
    description: i18n.t('meta.pages.root.description'),
  },
  list: {
    title: i18n.t('meta.pages.list.title'),
    description: i18n.t('meta.pages.list.description'),
  },
  schedule: {
    title: i18n.t('meta.pages.schedule.title'),
    description: i18n.t('meta.pages.schedule.description'),
  },
  search: {
    title: i18n.t('meta.pages.search.title'),
    description: i18n.t('meta.pages.search.description'),
  },
  property: {
    title: i18n.t('meta.pages.property.title'),
    description: i18n.t('meta.pages.property.description'),
  },
  rooms: {
    title: i18n.t('meta.pages.rooms.title'),
    description: i18n.t('meta.pages.rooms.description'),
  },
  planRooms: {
    title: i18n.t('meta.pages.rooms.title'),
    description: i18n.t('meta.pages.rooms.description'),
  },
  plans: {
    title: i18n.t('meta.pages.plans.title'),
    description: i18n.t('meta.pages.plans.description'),
  },
  propertyPlans: {
    title: i18n.t('meta.pages.plans.title'),
    description: i18n.t('meta.pages.plans.description'),
  },
  options: {
    title: i18n.t('meta.pages.options.title'),
    description: i18n.t('meta.pages.options.description'),
  },
  registrations: {
    root: {
      title: i18n.t('meta.pages.registrations.root.title'),
      description: i18n.t('meta.pages.registrations.root.description'),
    },
    confirmation: {
      title: i18n.t('meta.pages.registrations.confirmation.title'),
      description: i18n.t('meta.pages.registrations.confirmation.description'),
    },
    loading: {
      title: i18n.t('meta.pages.registrations.loading.title'),
      description: i18n.t('meta.pages.registrations.loading.description'),
    },
    completion: {
      title: i18n.t('meta.pages.registrations.completion.title'),
      description: i18n.t('meta.pages.registrations.completion.description'),
    },
  },
  reservations: {
    root: {
      title: i18n.t('meta.pages.reservations.root.title'),
      description: i18n.t('meta.pages.reservations.root.description'),
    },
    history: {
      title: i18n.t('meta.pages.reservations.history.title'),
      description: i18n.t('meta.pages.reservations.history.description'),
    },
    reservation: {
      title: i18n.t('meta.pages.reservations.reservation.title'),
      description: i18n.t('meta.pages.reservations.reservation.description'),
    },
    cancel: {
      root: {
        title: i18n.t('meta.pages.reservations.cancel.root.title'),
        description: i18n.t('meta.pages.reservations.cancel.root.description'),
      },
      completion: {
        title: i18n.t('meta.pages.reservations.cancel.completion.title'),
        description: i18n.t(
          'meta.pages.reservations.cancel.completion.description'
        ),
      },
    },
  },
  errors: {
    notFound: {
      title: i18n.t('meta.pages.errors.notFound.title'),
      description: i18n.t('meta.pages.errors.notFound.description'),
    },
    serverError: {
      title: i18n.t('meta.pages.errors.server.title'),
      description: i18n.t('meta.pages.errors.server.description'),
    },
    loginError: {
      title: i18n.t('meta.pages.errors.login.title'),
      description: i18n.t('meta.pages.errors.login.title'),
    },
  },
}

export enum EquipmentEnum {
  Bath = '0',
  GuestRoomWithOpenairbath = '1',
  Shower = '2',
  Toilet = '3',
  ToiletWithWashingMachine = '4',
  AirConditioner = '5',
  AirPurifier = '6',
  Humidifier = '7',
  Refrigerator = '8',
  Kitchen = '9',
  Minibar = '10',
  Coffee = '11',
  Tea = '12',
  ElectricKettle = '13',
  TV = '14',
  CableTV = '15',
  Telephone = '16',
  SafetyBox = '17',
  WiredInternet = '21',
  WifiInternet = '23',
  HairDryer = '24',
  TrouserPress = '25',
  Pajamas = '26',
  Slippers = '27',
  Towel = '29',
}

export enum AmenityEnum {
  ShampooConditioner = '0',
  BodySoap = '1',
  HandSoap = '2',
  Toothbrush = '3',
  FacialCleanser = '4',
  Cleansers = '5',
  Lotion = '6',
  MilkyLotion = '7',
  Shaver = '8',
  ShavingFoam = '9',
  HairBrush = '10',
  ShowerCap = '11',
  ClothingDeodorizingSpray = '12',
}

export enum FeatureEnum {
  Accessible = '0',
  NonSmoking = '1',
  Smoking = '2',
  PetsAllowed = '3',
}

export enum MealEnum {
  Breakfast = 1,
  Supper = 2,
  BreakfastSupper = 3,
  Lunch = 4,
  BreakfastLunch = 5,
  LunchSupper = 6,
  BreakfastLunchSupper = 7,
}

export enum RoomTypeEnum {
  Single = 'single',
  ThreeQuarter = 'three quarter',
  Double = 'double',
  Twin = 'twin',
  Triple = 'triple',
  FourBeds = 'four beds',
  FiveBeds = 'five beds',
  Others = 'others',
}

export enum GuestStatusEnum {
  PreArrival = 1,
  CheckInDay = 2,
  Stay = 3,
  CheckOutDay = 4,
  Departure = 5,
  NoShow = 6,
  CanceledByProperty = 7,
  CanceledByGuest = 8,
}

export type PathKey = keyof typeof PATH
export type Path = (typeof PATH)[PathKey]

export const BudgetLimits = [
  5000, 10000, 20000, 30000, 40000, 50000, 100000, 150000, 200000,
]

export const EFH_COUPON = {
  slug: 'efh',
  code: 'FVSTAY12',
  // クーポンコードを載せるクエリキー（#1896 で予約フローまで保持・自動適用される）
  queryKey: 'discount_code',
  image: '/img/banner-efh-coupon.png',
} as const

export type Area = (typeof AREA_VALUES)[number]

export type Equipment =
  | 'no_smoking'
  | 'smoking'
  | 'free_wifi'
  | 'parking'
  | 'spa'
  | 'restaurant'
  | 'pets_allowed'
  | 'family'

export type Order = 'order' | '-order' | 'distance' | '-distance'

export const RECEIPT_AVAILABILITY_REFRESH_INTERVAL = 24 * 60 * 60 * 1000

// sessionStorage key of the API token issued on login.
export const ACCESS_TOKEN_STORAGE_KEY = 'access_token'

// F-ID profile fields copied to sessionStorage on login (useLoginCallback),
// read back by AuthProvider and removed on logout (useLogout). Each name is
// shared by the token response field, the AuthContext field and the
// sessionStorage key, so adding a field here covers all three.
export const PROFILE_STORAGE_FIELDS = [
  'name',
  'kana',
  'email',
  'phone',
  'postalCode',
  'prefecture',
  'address1',
  'address2',
  'address3',
  'dateOfBirth',
] as const

export type ProfileStorageField = (typeof PROFILE_STORAGE_FIELDS)[number]

export const PROFILE_SESSION_STORAGE_KEYS = [
  ACCESS_TOKEN_STORAGE_KEY,
  ...PROFILE_STORAGE_FIELDS,
] as const
